import ManagedHiringSection from '@components/Dashboard/ManagedHiringSection';
import FollowUpsDue from '@components/Common/FollowUpsDue';
import SignupsByWeek from '@components/Dashboard/SignupsByWeek';
import SignupsSection from '@components/Dashboard/SignupsSection';
import NeedsAttention from '@components/Dashboard/NeedsAttention';
import Panel from '@components/Common/Panel';
import { isMarketingRole, isSuperAdmin, useCurrentUserRole } from '@components/Common/Utils/roles';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import StatTile from '@components/Common/StatTile';
import { Box, Typography } from '@mui/material';
import { fetchDashboardStatsRequest } from '@store/features/dashboard/actions';
import type { RootState } from '@store/rootReducer';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './index.scss';

const Dashboard: React.FC = () => {
  const dispatch = useDispatch();
  const { stats, loading: statsLoading } = useSelector((s: RootState) => s.dashboard);
  // Platform-wide totals are a Super-Admin tracking view; HRs see the managed program only.
  const role = useCurrentUserRole();
  const superAdmin = isSuperAdmin(role);
  /*
   * Marketing (owner, 2026-10-19) works on sign-ups: it gets the platform totals and the sign-ups
   * charts, and not the recruiter work (follow-ups, Premium Hiring) that is HR's.
   */
  const marketing = isMarketingRole(role);
  const platformView = superAdmin || marketing;

  useEffect(() => {
    dispatch(fetchDashboardStatsRequest());
  }, [dispatch]);

  // Each tile links to the list it counts. Deliberately the plain list, not a
  // filtered one: the list pages read filters as `filter_<display column name>`
  // (see `jobColMapping` in Jobs), so a deep link is keyed on a UI label and
  // breaks silently the day that label is reworded — landing the user on an
  // unfiltered page with no error. A tile whose number matches the page it
  // opens is worth more than a filter that quietly stops applying.
  const platformKpis = [
    {
      label: 'Total jobs',
      subLabel: stats?.activeJobs != null ? `${stats.activeJobs.toLocaleString()} active` : '-',
      value: stats?.totalJobs,
      icon: <WorkOutlineOutlinedIcon />,
      to: '/jobs',
      linkLabel: 'View all jobs',
    },
    {
      label: 'Job seekers',
      subLabel: stats?.newJobSeekers != null ? `+${stats.newJobSeekers.toLocaleString()} added this week` : '-',
      value: stats?.totalJobSeekers,
      icon: <PeopleOutlinedIcon />,
      to: '/jobseekers',
      linkLabel: 'View all job seekers',
    },
    {
      label: 'Employers',
      subLabel: stats?.newEmployers != null ? `+${stats.newEmployers.toLocaleString()} added this week` : '-',
      value: stats?.totalEmployers,
      icon: <BusinessOutlinedIcon />,
      to: '/employers',
      linkLabel: 'View all employers',
    },
  ];

  // Rendered inside the Analytics page (.an-page > .an-body.max-w-container), so this
  // returns a fragment — no own page/container wrapper — to inherit the same max-width,
  // page padding, and section gap as every other dashboard tab.
  return (
    <>
        {/* What is waiting on this person comes first: the rest of the page is what happened. */}
        <NeedsAttention />

        {/* ── Section: Platform KPIs (Super-Admin only — platform-wide business
             metrics, not an individual recruiter's progress) ─────────────────── */}
        {platformView && (
          <Panel className="db-section">
            <Box className="db-section-header">
              <Typography className="db-section-title" variant="subtitle1">
                Platform health
              </Typography>
              <Typography className="db-section-sub" variant="body2">
                Total registered users, jobs, and employer accounts
              </Typography>
            </Box>
            <Box className="cc-stat-grid">
              {platformKpis.map((kpi) => (
                <StatTile
                  key={kpi.label}
                  label={kpi.label}
                  value={kpi.value?.toLocaleString('en-IN') ?? '-'}
                  sublabel={kpi.subLabel}
                  icon={kpi.icon}
                  to={kpi.to}
                  linkLabel={kpi.linkLabel}
                  loading={statsLoading}
                />
              ))}
            </Box>
          </Panel>
        )}

        {/* Self sign-ups only. The tiles above count every record added, imports included,
            which is why they say "added" and this section exists. */}
        {platformView && <SignupsSection />}
        {platformView && <SignupsByWeek />}

        {/* What is due today comes before what happened: it is the part that needs doing. */}
        {!marketing && <FollowUpsDue />}

        {/* ── Section: Managed Hiring KPIs (C7) ───────────────────────────── */}
        {!marketing && <ManagedHiringSection />}
    </>
  );
};

export default Dashboard;
